import { useRef, useState } from 'react';
import type {
  RequiredTwoFactorSetupConfirmationResponse,
  TwoFactorChallengeResponse,
  TwoFactorSetupResponse,
} from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { Button } from '@/shared/components';
import { RecoveryCodesPanel, TwoFactorSetupPanel } from '@/features/account-security';
import { AuthAlert } from './AuthAlert';
import { AuthPageHeader } from './AuthPageHeader';
import { twoFactorFailure, type AuthFailure } from './loginFailure';

interface Props {
  challenge: TwoFactorChallengeResponse;
  onBack: () => void;
}

const steps = (now: 'connect' | 'codes') =>
  [
    { label: 'Anmelden', state: 'done' },
    { label: 'Verbinden', state: now === 'connect' ? 'now' : 'done' },
    { label: 'Codes sichern', state: now === 'codes' ? 'now' : 'next' },
  ] as const;

/**
 * Pflicht-Einrichtung der Zwei-Faktor-Anmeldung (Board A): der Workspace verlangt sie, eine
 * Sitzung entsteht erst nach den gesicherten Codes. Fehler stehen als Satz im Schritt.
 */
export function LoginRequiredTwoFactorSetup({ challenge, onBack }: Props) {
  const { api, acceptSession } = useAuth();
  const [setup, setSetup] = useState<TwoFactorSetupResponse | null>(null);
  const [completion, setCompletion] = useState<RequiredTwoFactorSetupConfirmationResponse | null>(
    null,
  );
  const [failure, setFailure] = useState<AuthFailure | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const attempt = async (work: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setFailure(null);
    try {
      await work();
    } catch (error) {
      setFailure(await twoFactorFailure(error, 'setup'));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const sentence = failure ? `${failure.title} ${failure.text}` : null;

  if (completion)
    return (
      <>
        <AuthPageHeader eyebrow="Zwei-Faktor" title="Codes sichern" steps={steps('codes')} />
        <RecoveryCodesPanel
          codes={completion.recoveryCodes}
          doneLabel="Anmeldung abschließen"
          loading={busy}
          failure={sentence}
          onDone={() =>
            void attempt(async () =>
              acceptSession(
                await api.auth.completeRequiredTwoFactorSetupSession(
                  completion.sessionChallengeToken,
                ),
              ),
            )
          }
        />
        <Button type="button" variant="ghost" size="lg" onClick={onBack}>
          Zurück zur Anmeldung
        </Button>
      </>
    );

  return (
    <>
      <AuthPageHeader
        eyebrow="Zwei-Faktor"
        title="App verbinden"
        steps={steps('connect')}
        description="Dein Workspace verlangt Zwei-Faktor. Eine Sitzung entsteht erst, wenn die App verbunden und die Codes gesichert sind."
      />
      {setup ? (
        <TwoFactorSetupPanel
          setup={setup}
          failure={sentence}
          onCancel={onBack}
          onConfirm={(code) =>
            attempt(async () =>
              setCompletion(
                await api.auth.completeRequiredTwoFactorSetup(setup.challengeToken, code),
              ),
            )
          }
        />
      ) : (
        <>
          {failure && <AuthAlert title={failure.title} text={failure.text} />}
          <Button
            variant="brand"
            size="lg"
            className="h-[54px] w-full text-base"
            loading={busy}
            onClick={() =>
              void attempt(async () =>
                setSetup(await api.auth.beginRequiredTwoFactorSetup(challenge.challengeToken)),
              )
            }
          >
            Einrichtung starten
          </Button>
          <Button type="button" variant="ghost" size="lg" onClick={onBack}>
            Zurück zur Anmeldung
          </Button>
        </>
      )}
    </>
  );
}
